import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/tokenService";
import defaultProfile from "../../../assets/Default_pfp.svg.png";
import Papa from "papaparse";
import { FaDownload } from "react-icons/fa";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/supervisor/users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = res.data?.users || res.data?.activeUsers || [];
        setUsers(list);
        setFiltered(list);
      } catch (err) {
        toast.error("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onSearch = (e) => {
    const q = e.target.value.toLowerCase();
    const f = users.filter((u) =>
      (u.full_Name || "").toLowerCase().includes(q) ||
      (u.employeeId || "").toString().includes(q)
    );
    setFiltered(f);
  };

  const downloadCSV = () => {
    if (!filtered.length) {
      toast.info("No data to export");
      return;
    }
    const selectedFields = filtered.map((u) => ({
      EmployeeId: u.employeeId ? `CMPL${u.employeeId}` : "",
      Name: u.full_Name,
      Email: u.email,
      Contact: u.contact,
      Aadhar: u.aadhar_number,
      DOB: u.dob,
      Gender: u.gender,
      Country: u.country,
      State: u.state,
      District: u.dist,
      House: u.house,
      Street: u.street,
      Landmark: u.landmark,
      PostOffice: u.po,
      ZIP: u.zip,
      Village: u.vtc,
      SubDistrict: u.subdist,
      CareOf: u.care_of,
      PAN: u.pan_number,
      PAN_Name: u.pan_name,
      Account_No: u.account_number,
      Account_Name: u.account_name,
      IFSC: u.account_ifsc,
      UAN: u.uan_number,
      EPF: u.epf_number,
      ESI: u.esi_number,
      ESIC: u.esic_number,
      DOJ: u.date_of_joining,
      Status: u.status,
      ExitDate: u.date_of_exiting,
      Basic: u.basic,
      DA: u.da,
      HRA: u.hra,
      OtherAllowance: u.other_allowance,
      LeaveWithWages: u.leave_with_wages,
      KYC: u.kyc ? "Verified" : "Pending",
      PAN_Status: u.pan_added ? "Uploaded" : "Pending",
      Account_Status: u.account_added ? "Uploaded" : "Pending",
      UAN_ESIC_Status: u.uan_esic_added ? "Uploaded" : "Pending",
      PF_ESIC_Status: u.pf_esic_status ? "Completed" : "Pending",
      Wages_Status: u.wages_status ? "Completed" : "Pending",
      Active_Status: u.active_user_status ? "Active" : "Inactive",
      Face_Status: u.faceAdded ? "Uploaded" : "Pending",
    }));
    const csv = Papa.unparse(selectedFields);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "supervisor_employees.csv";
    a.click();
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <h4 className="mb-0">Employees</h4>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or ID"
            onChange={onSearch}
            style={{ minWidth: "220px" }}
          />
          <button className="btn btn-outline-secondary" onClick={downloadCSV}>
            <FaDownload className="me-1" /> Export
          </button>
        </div>
      </div>
      {!filtered.length ? (
        <p className="text-muted">No employees found.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead className="table-light">
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Aadhar</th>
                <th>DOB</th>
                <th>Gender</th>
                <th>Country</th>
                <th>State</th>
                <th>District</th>
                <th>House</th>
                <th>Street</th>
                <th>Landmark</th>
                <th>Post Office</th>
                <th>ZIP</th>
                <th>Village</th>
                <th>SubDistrict</th>
                <th>Care Of</th>
                <th>PAN</th>
                <th>PAN Name</th>
                <th>Account No</th>
                <th>Account Name</th>
                <th>IFSC</th>
                <th>UAN</th>
                <th>EPF</th>
                <th>ESI</th>
                <th>ESIC</th>
                <th>DOJ</th>
                <th>Status</th>
                <th>Exit Date</th>
                <th>Basic</th>
                <th>DA</th>
                <th>HRA</th>
                <th>Other Allow.</th>
                <th>Leave w/ Wages</th>
                <th>KYC</th>
                <th>PAN Status</th>
                <th>Account Status</th>
                <th>UAN/ESIC Status</th>
                <th>PF/ESIC Status</th>
                <th>Wages Status</th>
                <th>Active Status</th>
                <th>Face Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u._id}>
                  <td>{u.employeeId ? `CMPL${u.employeeId}` : "N/A"}</td>
                  <td className="fw-semibold">{u.full_Name || "N/A"}</td>
                  <td>{u.email || ""}</td>
                  <td>{u.contact || ""}</td>
                  <td>{u.aadhar_number || ""}</td>
                  <td>{u.dob || ""}</td>
                  <td>{u.gender || ""}</td>
                  <td>{u.country || ""}</td>
                  <td>{u.state || ""}</td>
                  <td>{u.dist || ""}</td>
                  <td>{u.house || ""}</td>
                  <td>{u.street || ""}</td>
                  <td>{u.landmark || ""}</td>
                  <td>{u.po || ""}</td>
                  <td>{u.zip || ""}</td>
                  <td>{u.vtc || ""}</td>
                  <td>{u.subdist || ""}</td>
                  <td>{u.care_of || ""}</td>
                  <td>{u.pan_number || ""}</td>
                  <td>{u.pan_name || ""}</td>
                  <td>{u.account_number || ""}</td>
                  <td>{u.account_name || ""}</td>
                  <td>{u.account_ifsc || ""}</td>
                  <td>{u.uan_number || ""}</td>
                  <td>{u.epf_number || ""}</td>
                  <td>{u.esi_number || ""}</td>
                  <td>{u.esic_number || ""}</td>
                  <td>{u.date_of_joining ? new Date(u.date_of_joining).toLocaleDateString() : ""}</td>
                  <td>
                    <span className={`badge ${
                      u.status === 'Active' ? 'bg-success' : 
                      u.status === 'Exited' ? 'bg-warning' : 
                      u.status === 'Terminated' ? 'bg-danger' : 'bg-secondary'
                    }`}>
                      {u.status || "N/A"}
                    </span>
                  </td>
                  <td>{u.date_of_exiting ? new Date(u.date_of_exiting).toLocaleDateString() : ""}</td>
                  <td>{u.basic || ""}</td>
                  <td>{u.da || ""}</td>
                  <td>{u.hra || ""}</td>
                  <td>{u.other_allowance || ""}</td>
                  <td>{u.leave_with_wages || ""}</td>
                  <td>
                    <span className={`badge ${u.kyc ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.kyc ? "Verified" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.pan_added ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.pan_added ? "Uploaded" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.account_added ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.account_added ? "Uploaded" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.uan_esic_added ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.uan_esic_added ? "Uploaded" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.pf_esic_status ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.pf_esic_status ? "Completed" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.wages_status ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.wages_status ? "Completed" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.active_user_status ? 'bg-success' : 'bg-secondary'}`}>
                      {u.active_user_status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.faceAdded ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {u.faceAdded ? "Uploaded" : "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Users;
